import { Component, inject, signal, computed, effect, input, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  ClientService,
  ClientApiKey,
  ClientApiKeyDto,
  ApiKeyEnvironment,
  ClientBranch,
} from '../../../services/client';
import { DropdownItem } from '../../../services/dropdown';
import { ToastService } from '../../../services/toast';
import { IndDatePipe } from '../../../pipes/ind-date';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-client-api-keys',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    SelectModule,
    ToggleSwitchModule,
    IndDatePipe,
  ],
  templateUrl: './client-api-keys.html',
  styleUrls: ['../clients-shared.css', './client-api-keys.css'],
})
export class ClientApiKeysTab implements OnInit {
  clientId = input.required<number>();

  private fb = inject(FormBuilder);
  private svc = inject(ClientService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  // Env options come from GET api/ClientApiKey/Environments; seeded with sensible defaults
  // so the dropdown still works if that call hasn't returned yet.
  environmentOptions = signal<DropdownItem[]>([
    { value: 'Test', label: 'Test' },
    { value: 'Prod', label: 'Prod' },
    { value: 'Live', label: 'Live' },
  ]);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  rows = signal<ClientApiKey[]>([]);

  // A key is either an ACCOUNT key (all branches, narrowable per request) or LOCKED to one branch.
  // DropdownItem.value can't be null, so 0 is the "account / no branch binding" sentinel (mapped to
  // null in the DTO on save).
  branches = signal<ClientBranch[]>([]);
  branchOptions = computed<DropdownItem[]>(() => [
    { value: 0, label: 'Account — all branches (full access)' },
    ...this.branches().map((b) => ({
      value: b.id,
      label:
        (b.isDefault ? `${b.branchName} (default)` : `${b.branchName} (${b.branchCode})`) +
        (b.isActive ? '' : ' — inactive'),
    })),
  ]);

  // One-time full secret panel shown right after Insert / Regenerate (the secret is never returned again).
  revealedSecret = signal<string | null>(null);
  revealedKey = signal<string | null>(null);

  // Embed-widget dialog (public key + allowed domains + copy-paste snippet for the client's website).
  showEmbed = signal(false);
  embedRow = signal<ClientApiKey | null>(null);

  form: FormGroup = this.fb.group({
    id: [0],
    branchId: [0], // 0 = account key (all branches); a branch id locks the key to it
    environment: [null as ApiKeyEnvironment | null, Validators.required],
    allowedIPs: [''],
    embedAllowedOrigins: [''], // CSV of website origins the enrollment widget may run from
    rateLimitPerMinute: [null as number | null],
    expiryDate: [null as Date | null],
    isActive: [true],
  });

  constructor() {
    // load() reads clientId(), so this effect reloads whenever the bound client changes —
    // the tab component is kept alive when navigating between client detail pages, so a
    // one-shot ngOnInit load would keep showing the previous client's keys.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.load();
    });
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.loadEnvironments();
  }

  private loadEnvironments() {
    this.svc.keyEnvironments().subscribe({
      next: (opts) => {
        if (opts?.length) this.environmentOptions.set(opts);
      },
    });
  }

  load() {
    this.loading.set(true);
    this.svc.keyGetByClient(this.clientId()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
    this.loadBranches();
  }

  private loadBranches() {
    this.svc.brGetByClient(this.clientId()).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? [];
        // Keep INACTIVE branches too (labeled) so editing a key already bound to a now-inactive branch
        // still shows its real scope instead of silently reading as "Account — all branches".
        const list = (Array.isArray(data) ? data : []) as ClientBranch[];
        list.sort(
          (a, b) => Number(b.isDefault) - Number(a.isDefault) || Number(b.isActive) - Number(a.isActive),
        );
        this.branches.set(list);
      },
    });
  }

  // ===================== display / secret helpers =====================
  envSeverity(env?: string | null): 'success' | 'warn' | 'info' {
    switch (env) {
      case 'Live':
        return 'success'; // green
      case 'Prod':
        return 'info'; // blue
      default:
        return 'warn'; // Test — amber
    }
  }

  /** Mask a credential but keep the readable env prefix (e.g. "lapitch_live_••••1234"). */
  mask(v?: string | null): string {
    if (!v) return '—';
    if (v.length <= 12) return v.slice(0, 2) + '••••';
    return v.slice(0, Math.min(13, v.length - 4)) + '••••' + v.slice(-4);
  }

  async copy(v?: string | null) {
    if (!v || !isPlatformBrowser(this.platformId)) return;
    try {
      await navigator.clipboard.writeText(v);
      this.toast.success('Copied to clipboard');
    } catch {
      this.toast.error('Could not copy to clipboard');
    }
  }

  dismissRevealed() {
    this.revealedSecret.set(null);
    this.revealedKey.set(null);
  }

  // ===================== embed widget =====================
  openEmbed(k: ClientApiKey) {
    this.embedRow.set(k);
    this.showEmbed.set(true);
  }

  closeEmbed() {
    this.showEmbed.set(false);
  }

  /** The copy-paste snippet the client drops on their website (uses the configured Client API origin). */
  embedSnippet(k: ClientApiKey | null): string {
    if (!k?.publicEmbedKey) return '';
    const origin = (environment.clientApiUrl || '').replace(/\/+$/, '');
    return [
      `<link rel="stylesheet" href="${origin}/embed/enroll.css">`,
      `<script src="${origin}/embed/enroll.js" data-key="${k.publicEmbedKey}" defer></script>`,
      '',
      '<!-- Put data-arogya-enroll on any button, link, or logo: -->',
      '<button data-arogya-enroll>Enroll now</button>',
    ].join('\n');
  }

  // ===================== add / edit =====================
  openAdd() {
    this.editing.set(false);
    this.form.reset({
      id: 0,
      branchId: 0, // account key (all branches) by default
      environment: null,
      allowedIPs: '',
      embedAllowedOrigins: '',
      rateLimitPerMinute: null,
      expiryDate: null,
      isActive: true,
    });
    this.form.get('environment')?.enable(); // chosen at creation
    this.showModal.set(true);
  }

  openEdit(k: ClientApiKey) {
    this.editing.set(true);
    this.form.reset({
      id: k.id,
      branchId: k.branchId ?? 0,
      environment: k.environment,
      allowedIPs: k.allowedIPs ?? '',
      embedAllowedOrigins: k.embedAllowedOrigins ?? '',
      rateLimitPerMinute: k.rateLimitPerMinute ?? null,
      expiryDate: this.parseDate(k.expiryDate),
      isActive: k.isActive ?? true,
    });
    // Environment is baked into the key — fixed at creation, read-only on edit.
    this.form.get('environment')?.disable();
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Environment is required.');
      return;
    }
    const v = this.form.getRawValue();
    const dto: ClientApiKeyDto = {
      id: this.editing() ? Number(v.id) : 0,
      clientId: this.clientId(),
      branchId: Number(v.branchId) > 0 ? Number(v.branchId) : null, // 0/null = account key (all branches)
      environment: v.environment,
      allowedIPs: this.nz(v.allowedIPs),
      embedAllowedOrigins: this.nz(v.embedAllowedOrigins),
      // Non-nullable int server-side: send 0 when blank (the API treats <=0 as its default) — never null,
      // which would fail model binding with a raw 400.
      rateLimitPerMinute: v.rateLimitPerMinute != null ? Number(v.rateLimitPerMinute) : 0,
      expiryDate: this.toIso(v.expiryDate) || null,
      isActive: !!v.isActive,
    } as ClientApiKeyDto;

    this.saving.set(true);
    this.svc.keySave(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save API key');
          return;
        }
        // On insert the backend issues the key + secret — surface the new secret once if returned.
        const issued = res?.data;
        if (!this.editing() && issued?.apiSecret) {
          this.revealedKey.set(issued.apiKey ?? null);
          this.revealedSecret.set(issued.apiSecret);
        }
        this.toast.success(res?.message || (this.editing() ? 'API key updated' : 'API key created'));
        this.closeModal();
        this.load();
      },
      error: (err: any) => {
        this.saving.set(false);
        this.toast.error(err?.error?.message || 'Failed to save API key');
      },
    });
  }

  // ===================== regenerate =====================
  async regenerate(k: ClientApiKey) {
    const ok = await this.toast.confirm(
      'The current key & secret stop working immediately. Any integration using them must be updated.',
      'Regenerate API credentials?',
      'Regenerate',
    );
    if (!ok) return;
    this.svc.keyRegenerate(k.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to regenerate credentials');
          return;
        }
        const d = res?.data ?? {};
        this.revealedKey.set(d.apiKey ?? null);
        this.revealedSecret.set(d.apiSecret ?? null);
        this.toast.success('Credentials regenerated — copy the new secret now.');
        this.load();
      },
      error: () => this.toast.error('Failed to regenerate credentials'),
    });
  }

  // ===================== row actions =====================
  toggleActive(k: ClientApiKey) {
    const next = !k.isActive;
    this.patchRow(k.id, { isActive: next });
    this.svc.keySetActive(k.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(k.id, { isActive: k.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(k.id, { isActive: k.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  async remove(k: ClientApiKey) {
    const ok = await this.toast.confirm(
      `The ${k.environment} API key will be deleted and stop working immediately.`,
      'Delete API key?',
      'Delete',
    );
    if (!ok) return;
    this.svc.keyDelete(k.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete API key');
          return;
        }
        this.toast.success(res?.message || 'API key deleted');
        this.load();
      },
    });
  }

  // ===================== utils =====================
  private patchRow(id: number, patch: Partial<ClientApiKey>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  private nz(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }

  private parseDate(v: any): Date | null {
    if (!v) return null;
    const d = new Date(String(v).slice(0, 10) + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }

  private toIso(v: any): string {
    if (!v) return '';
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
